import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createInterstitialProvider } from '../ads/interstitialProviders';
import { config } from '../config';
import { trackInterstitialAdViewed } from '../analytics';
import { isLikelyVignetteNode } from '../ads/vignetteObserver';
import { logInterstitialStage } from '../ads/interstitialLogger';
import { useGoogleCmp, canShowGoogleInterstitial } from '../utils/googleCmp';

const BLOCKED_INPUT_EVENTS = [
  'keydown',
  'keypress',
  'keyup',
  'click',
  'pointerdown',
  'pointerup',
  'mousedown',
  'mouseup',
  'touchstart',
  'touchend',
  'submit',
];

const ADS_BOOTSTRAP_STATE_KEY = '__GDP_ADS_BOOTSTRAP__';
const ADS_BOOTSTRAP_STATUS_EVENT = 'gdp:ads-bootstrap-status';

const defaultContextValue = {
  showAds: false,
  shouldShowLiveAds: false,
  shouldShowFakeBanner: false,
  adsScriptStatus: 'unknown',
  areAdsBlocked: false,
  isAdBreakActive: false,
  adProviderType: 'DISABLED',
  navigationInterstitialMode: 'blocking',
  registerNavigationInterstitialCandidate: () => {},
  requestGameInterstitial: async () => ({ shown: false, reason: 'ads_disabled' }),
  requestNavigationInterstitial: async () => ({ shown: false, reason: 'ads_disabled' }),
  requestRewardedAd: async () => ({ shown: false, reason: 'ads_disabled' }),
};

const AdsContext = createContext(defaultContextValue);

const isAdminPortalBuild = process.env.REACT_APP_IS_ADMIN_PORTAL === 'true';

const normalizeEnvironment = (value) => String(value || '').trim().toLowerCase();
const normalizeAdProvider = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  if (normalized === 'GPT' || normalized === 'H5' || normalized === 'VIGNETTE' || normalized === 'DISABLED') {
    return normalized;
  }
  return 'H5';
};

const shouldLogInterstitialEvent = (activeProviderType, details = {}) => {
  const eventProviderType = String(
    details?.providerType || details?.provider_type || ''
  ).trim().toUpperCase();

  if (!eventProviderType) {
    return true;
  }

  return eventProviderType === String(activeProviderType || '').trim().toUpperCase();
};

const getAdsBootstrapState = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const state = window[ADS_BOOTSTRAP_STATE_KEY];
  if (!state || typeof state !== 'object' || Array.isArray(state)) {
    return null;
  }

  return state;
};

const getAdsScriptStatus = () => getAdsBootstrapState()?.scriptStatus || 'unknown';

const isAdBreakStub = () => (
  typeof window !== 'undefined'
  && typeof window.adBreak === 'function'
  && window.adBreak.__GDP_STUB__ === true
);

const blockInputEvent = (event) => {
  if (typeof event.preventDefault === 'function') {
    event.preventDefault();
  }
  if (typeof event.stopPropagation === 'function') {
    event.stopPropagation();
  }
  if (typeof event.stopImmediatePropagation === 'function') {
    event.stopImmediatePropagation();
  }
};

export function AdsProvider({ children }) {
  const cmp = useGoogleCmp();
  const showAds = config.SHOW_ADS === true && !isAdminPortalBuild;
  const isProductionEnvironment = normalizeEnvironment(config.ENVIRONMENT) === 'production';
  const shouldShowLiveAds = showAds && isProductionEnvironment && cmp.adsReady;
  const shouldShowFakeBanner = showAds && !isProductionEnvironment;
  const adProviderType = showAds ? normalizeAdProvider(config.AD_PROVIDER) : 'DISABLED';
  const navigationInterstitialMode = adProviderType === 'GPT' || adProviderType === 'VIGNETTE'
    ? 'anchor'
    : 'blocking';
  const [isAdBreakActive, setIsAdBreakActive] = useState(false);
  const [adsScriptStatus, setAdsScriptStatus] = useState(() => getAdsScriptStatus());
  const interstitialInFlightRef = useRef(false);
  const providerRef = useRef(null);
  const areAdsBlocked = adsScriptStatus === 'blocked';
  const lastEligibleNavigationRef = useRef(null);
  const vignetteLifecycleStateRef = useRef({
    isActive: false,
    lastSeenAt: null,
  });

  if (!providerRef.current || providerRef.current.type !== adProviderType) {
    providerRef.current = {
      type: adProviderType,
      instance: createInterstitialProvider({
        providerType: adProviderType,
        getAdsScriptStatus,
        isProductionEnvironment,
      }),
    };
  }

  useEffect(() => {
    if (!isAdBreakActive || typeof window === 'undefined') {
      return undefined;
    }

    BLOCKED_INPUT_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, blockInputEvent, true);
    });

    return () => {
      BLOCKED_INPUT_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, blockInputEvent, true);
      });
    };
  }, [isAdBreakActive]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncAdsScriptStatus = (event) => {
      const nextStatus = event?.detail?.scriptStatus || getAdsScriptStatus();
      setAdsScriptStatus(nextStatus);
    };

    syncAdsScriptStatus();
    window.addEventListener(ADS_BOOTSTRAP_STATUS_EVENT, syncAdsScriptStatus);

    return () => {
      window.removeEventListener(ADS_BOOTSTRAP_STATUS_EVENT, syncAdsScriptStatus);
    };
  }, []);

  useEffect(() => {
    logInterstitialStage('provider_selected', {
      providerType: adProviderType,
      showAds,
      shouldShowLiveAds,
      shouldShowFakeBanner,
      environment: normalizeEnvironment(config.ENVIRONMENT),
      adsScriptStatus,
    });
    if (showAds && canShowGoogleInterstitial()) {
      providerRef.current?.instance?.initialize?.();
    } else {
      providerRef.current?.instance?.suspend?.();
    }
  }, [adProviderType, adsScriptStatus, shouldShowFakeBanner, shouldShowLiveAds, showAds, cmp.siteConsentOpen]);

  const createRequestDetails = useCallback(({ gameSlug, date, exitType, targetPath, outcome, placementName: providedPlacementName }) => {
    const placementName = providedPlacementName || `${gameSlug || 'game'}_${exitType || outcome || 'interstitial'}_${date || 'unknown'}`;
    return {
      placementName,
      gameSlug: gameSlug || null,
      date: date || null,
      exitType: exitType || null,
      targetPath: targetPath || null,
      outcome: outcome || null,
      providerType: adProviderType,
      environment: normalizeEnvironment(config.ENVIRONMENT),
      isProductionEnvironment,
      scriptStatus: adsScriptStatus,
      adBreakStubActive: isAdBreakStub(),
      hasAdBreakApi: typeof window !== 'undefined' && typeof window.adBreak === 'function',
      hasGoogletagApi: typeof window !== 'undefined' && typeof window.googletag === 'object',
      hasAdsQueue: typeof window !== 'undefined' && Array.isArray(window.adsbygoogle),
    };
  }, [adProviderType, adsScriptStatus, isProductionEnvironment]);

  const registerNavigationInterstitialCandidate = useCallback((details) => {
    const requestDetails = createRequestDetails(details || {});
    lastEligibleNavigationRef.current = {
      ...requestDetails,
      capturedAt: Date.now(),
    };
    logInterstitialStage('navigation_candidate_registered', requestDetails);
    return requestDetails;
  }, [createRequestDetails]);

  useEffect(() => {
    if (!showAds || adProviderType !== 'VIGNETTE' || typeof document === 'undefined' || !document.body) {
      return undefined;
    }

    const observer = new MutationObserver((mutations) => {
      const lifecycleState = vignetteLifecycleStateRef.current;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!isLikelyVignetteNode(node) || lifecycleState.isActive) {
            return;
          }

          lifecycleState.isActive = true;
          lifecycleState.lastSeenAt = Date.now();
          const lastContext = lastEligibleNavigationRef.current || {};

          trackInterstitialAdViewed({
            source_surface: 'ads_interstitial',
            game_slug: lastContext.gameSlug || null,
            game_date: lastContext.date || null,
            placement_name: lastContext.placementName || null,
            provider_type: 'VIGNETTE',
            exit_type: lastContext.exitType || null,
            target_path: lastContext.targetPath || null,
            source_stage: 'mutation_observer',
          });
          logInterstitialStage('vignette_detected', {
            ...lastContext,
            sourceStage: 'mutation_observer',
          });
        });

        mutation.removedNodes.forEach((node) => {
          if (!isLikelyVignetteNode(node) || !lifecycleState.isActive) {
            return;
          }

          lifecycleState.isActive = false;
          logInterstitialStage('vignette_cleared', {
            removedAt: Date.now(),
            lastSeenAt: lifecycleState.lastSeenAt,
          });
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      vignetteLifecycleStateRef.current.isActive = false;
    };
  }, [adProviderType, showAds]);

  const requestNavigationInterstitial = useCallback(async ({ gameSlug, date, exitType, targetPath, outcome, placementName: providedPlacementName }) => {
    if (!canShowGoogleInterstitial()) return { shown: false, reason: 'consent_pending' };
    const provider = providerRef.current?.instance;
    const requestDetails = registerNavigationInterstitialCandidate({
      gameSlug,
      date,
      exitType,
      targetPath,
      outcome,
      placementName: providedPlacementName,
    });

    if (interstitialInFlightRef.current) {
      logInterstitialStage('skipped', {
        ...requestDetails,
        reason: 'interstitial_in_flight',
      });
      return { shown: false, reason: 'interstitial_in_flight' };
    }

    interstitialInFlightRef.current = true;
    setIsAdBreakActive(true);

    const startedAt = Date.now();
    logInterstitialStage('navigation_request_started', requestDetails);

    try {
      const result = await provider.showInterstitial({
        requestDetails,
        onEvent: (level, stage, details = {}) => {
          if (!shouldLogInterstitialEvent(adProviderType, details)) {
            return;
          }
          logInterstitialStage(stage, details);
        },
      });

      logInterstitialStage('resolved', {
        ...requestDetails,
        elapsedMs: Date.now() - startedAt,
        shown: result?.shown === true,
        reason: result?.reason || null,
        placementInfo: result?.placementInfo || null,
      });

      return result;
    } catch (error) {
      logInterstitialStage('request_failed', {
        ...requestDetails,
        error: error instanceof Error
          ? {
              name: error.name,
              message: error.message,
            }
          : String(error),
      });
      return {
        shown: false,
        reason: 'request_failed',
      };
    } finally {
      interstitialInFlightRef.current = false;
      setIsAdBreakActive(false);
      logInterstitialStage('navigation_request_finished', {
        ...requestDetails,
        elapsedMs: Date.now() - startedAt,
      });
    }
  }, [registerNavigationInterstitialCandidate]);

  const requestRewardedAd = useCallback(async ({
    gameSlug,
    placementName,
    sourceSurface,
    onBeforeReward,
    onBeforeAd,
    onAdViewed,
    onAdDismissed,
    onAfterAd,
    onAdBreakDone,
  }) => {
    if (!canShowGoogleInterstitial()) return { shown: false, rewardViewed: false, reason: 'consent_pending' };
    const provider = providerRef.current?.instance;
    const requestDetails = createRequestDetails({
      gameSlug,
      placementName: placementName || `${sourceSurface || 'rewarded'}_rewarded_ad`,
      outcome: 'rewarded_ad',
    });

    if (!provider || typeof provider.showRewarded !== 'function') {
      logInterstitialStage('rewarded_request_unsupported', {
        ...requestDetails,
        reason: 'provider_missing_rewarded_support',
      });
      return {
        shown: false,
        reason: 'provider_missing_rewarded_support',
      };
    }

    if (interstitialInFlightRef.current) {
      logInterstitialStage('rewarded_skipped', {
        ...requestDetails,
        reason: 'interstitial_in_flight',
      });
      return { shown: false, reason: 'interstitial_in_flight' };
    }

    interstitialInFlightRef.current = true;
    setIsAdBreakActive(true);
    const startedAt = Date.now();
    logInterstitialStage('rewarded_request_started', requestDetails);

    try {
      const result = await provider.showRewarded({
        requestDetails,
        onBeforeReward,
        onBeforeAd,
        onAdViewed,
        onAdDismissed,
        onAfterAd,
        onAdBreakDone,
        onEvent: (level, stage, details = {}) => {
          if (!shouldLogInterstitialEvent(adProviderType, details)) {
            return;
          }
          logInterstitialStage(stage, details);
        },
      });

      logInterstitialStage('rewarded_resolved', {
        ...requestDetails,
        elapsedMs: Date.now() - startedAt,
        shown: result?.shown === true,
        rewardViewed: result?.rewardViewed === true,
        reason: result?.reason || null,
        placementInfo: result?.placementInfo || null,
      });

      return result;
    } catch (error) {
      logInterstitialStage('rewarded_request_failed', {
        ...requestDetails,
        error: error instanceof Error
          ? {
              name: error.name,
              message: error.message,
            }
          : String(error),
      });
      return {
        shown: false,
        reason: 'request_failed',
      };
    } finally {
      interstitialInFlightRef.current = false;
      setIsAdBreakActive(false);
      logInterstitialStage('rewarded_request_finished', {
        ...requestDetails,
        elapsedMs: Date.now() - startedAt,
      });
    }
  }, [adProviderType, createRequestDetails]);

  const value = useMemo(() => ({
    showAds,
    shouldShowLiveAds,
    shouldShowFakeBanner,
    adsScriptStatus,
    areAdsBlocked,
    isAdBreakActive,
    adProviderType,
    navigationInterstitialMode,
    registerNavigationInterstitialCandidate,
    requestGameInterstitial: requestNavigationInterstitial,
    requestNavigationInterstitial,
    requestRewardedAd,
  }), [
    adProviderType,
    adsScriptStatus,
    areAdsBlocked,
    isAdBreakActive,
    navigationInterstitialMode,
    registerNavigationInterstitialCandidate,
    requestNavigationInterstitial,
    requestRewardedAd,
    shouldShowFakeBanner,
    shouldShowLiveAds,
    showAds,
  ]);

  return (
    <AdsContext.Provider value={value}>
      {children}
    </AdsContext.Provider>
  );
}

export function useAds() {
  return useContext(AdsContext);
}
